import React from "react";
import { T } from "../../../lib/i18n";

/**
 * 「签约日期」待确认数量缓存（保留但已不再用于渲染）
 * DesignV3SignDates 保存后会调 bustSignCount() 作废它；页面顶部不再渲染快捷菜单角标后，
 * 这里实际只是空转 —— 保留是为了不动调用点，将来若把该角标挂回左侧边栏菜单项可直接复用。
 */
let _signCountCache = { at: 0, n: 0 };
export const bustSignCount = () => { _signCountCache = { at: 0, n: 0 }; };

/**
 * 设计部 v3 页面头部（2026-10-16 瘦身）
 *
 * 变更原因（用户需求）：
 *   原先这里是三行 ——「ALIGCUS DESIGN TRACKER · V3」紫色 kicker + 页面说明 + **一排 12 个快捷菜单**
 *   （总览仪表盘 / 项目管理 / 签约前营销方案 / 待办任务 / 审批中心 / 签约日期 / 结构外包 / 团队分工 /
 *   自动化中心 / AI 管理助手 / 集成与设置 / 绩效报表）。这排快捷菜单与左侧边栏**完全重复**，
 *   却占掉约 60px 高度，把下面的表格挤矮（1440×900 下表格可视区只剩 ~320px）。
 *   用户要求：设计部所有页面顶部这块都处理掉。
 *
 * 现在整个头部只剩「右侧操作按钮插槽」一行（没有 right 的页面则整块不占高度）：
 *   · 页面说明小字（sub，如「62 / 62 个已签约设计项目 · 点击卡片打开抽屉…」）也一并去掉
 *     —— 2026-10-16 用户第二轮要求「页面顶部的这种类型的小字也可以不显示」，设计部每个页面都有这行；
 *   · 页面标题（h1）保留但仍是 sr-only —— 可见标题由面包屑承担（见 index.css 约定）；
 *   · 各页面仍在传的 sub / counts / signDatesTick prop 不再解构使用 —— 保留调用点不动，
 *     避免改 11 个页面（传进来被忽略，无副作用）。
 */
export const V3Header = ({ title, right }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="dv3-header">
    <h1 className="sr-only" data-testid="dv3-title">{title}</h1>
    {right && <div className="flex flex-wrap items-center gap-2 ml-auto">{right}</div>}
  </div>
);

/**
 * 设计部页面「进不去」时的提示。
 *
 * 2026-10-20（修「有页面权限仍显示无权限」）：原实现把 403 与加载失败合成一句
 * 「无权限访问设计部或加载失败。」，管理员无法判断到底是被权限拦住、还是后端出错，
 * 只能来回试。现在按状态码分开说：
 *   · 403（status === 403）→ 明确说没权限，并指出去哪里开权限（角色权限矩阵 / 账号菜单权限）；
 *   · 其它（网络断开、后端 5xx、超时）→ 说加载失败并提示重试，不再误导成权限问题。
 * 不传 status 时保留原来的合并文案，兼容既有调用点。
 */
export const Denied = ({ status }) => {
  const isDenied = status === 403;
  const text = status === undefined
    ? T("无权限访问设计部或加载失败。")
    : isDenied
      ? T("你没有设计部页面的访问权限。请联系管理员在「权限管理 → 角色权限矩阵 / 账号菜单权限」中开通。")
      : T("设计部数据加载失败，请稍后重试或检查网络。");
  return <div className="p-6 text-sm text-slate-500 bg-white rounded-lg border border-slate-200" data-testid="dv3-denied" data-status={status || ""}>{text}</div>;
};

export const Panel = ({ title, icon: Icon, children, tid, extra, className = "", onClick, title2 }) => (
  <div className={`bg-white rounded-lg border border-slate-200 p-4 min-w-0 ${onClick ? "group cursor-pointer hover:border-slate-400 hover:shadow-sm transition-colors" : ""} ${className}`} data-testid={tid}
    onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} title={title2}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}>
    {(title || extra) && (
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">{Icon && <Icon className="h-4 w-4 text-violet-500" />}{title}</p>
        {extra}
      </div>
    )}
    {children}
  </div>
);
